const frequencyShift = {
  slug: "frequency-shift",
  layout: "frequencyShift",
  title: "Frequency Shift",
  category: "Creative Direction / Web / Video",
  client: "Frequency Shift",
  duration: "Ongoing",
  date: "2025 — Present",
  location: "Ottawa, Canada",
  cover: "/assets/frequency-shift/fs-icon-vector.svg",
  gallery: [],
  relatedSlugs: ["video-production", "trendless", "menu-design"],
  liveUrl: "https://frequencyshift.ca/",
  instagramUrl: "https://www.instagram.com/frequency___shift/",
  content: {
    hero: {
      kicker: "Creative Direction / Web Management / Video",
      dek: "I run the creative side of Frequency Shift. I direct the look, build and manage the website, and edit the videos for each event. I’m also on the core team, so I help with planning and promotion and work the nights themselves.",
      image: "/assets/frequency-shift/hero-crowd.webp",
      meta: [
        { label: "Role", value: "Creative Direction" },
        { label: "Platform", value: "frequencyshift.ca" },
        { label: "Output", value: "Web Development / Video Editing" },
        { label: "Period", value: "2025—Present" },
      ],
    },
    overview: {
      eyebrow: "02 / The role",
      title: "One role across the whole series.",
      paragraphs: [
        "Frequency Shift is a house series in Ottawa. It runs solo nights, partner takeovers and a two-stage festival.",
        "I make the visuals, look after frequencyshift.ca and edit the videos, so the festival and a one-off partner night still look like the same series. I also keep each campaign on schedule, from the first announcement to the night itself to the archive afterwards.",
      ],
      annotation: "For the love of house.",
    },
    creative: {
      eyebrow: "03 / Creative direction",
      title: "A visual system built to move between rooms.",
      paragraphs: [
        "Every night needs something a bit different. The first solo night at GRIDWRKS, the collaborations at City At Night and Frequency Fest at Club SAW each got their own look. The base stays the same: bold type, saturated colour, the FS mark and fast, high-contrast motion. From there I adjust it for the partner, the lineup and the venue.",
        "The photos matter as much as the graphics. They show who actually comes and where it happens.",
      ],
    },
    featuredPosts: [
      {
        title: "Frequency Fest Vol. 1",
        venue: "Club SAW",
        credit: "Photo: Catherine Archambault",
        src: "/assets/frequency-shift/photos/frequency-fest-04.webp",
        alt: "A dancer in silver sunglasses moving through the warm amber Frequency Fest crowd at Club SAW.",
        href: "https://www.instagram.com/p/Das9FdvCfZt/",
      },
      {
        title: "Frequency Shift 001",
        venue: "Poa Tiki",
        credit: "Photo: Blue Chapel Lamb",
        src: "/assets/frequency-shift/photos/frequency-shift-001-01.webp",
        alt: "The first Frequency Shift dancefloor glowing red, blue, and white beneath Poa Tiki’s rope ceiling.",
        href: "https://www.instagram.com/p/DKe8HNiOT1M/",
      },
      {
        title: "Frequency Shift 003",
        venue: "GRIDWRKS",
        credit: "Photo credit not listed",
        src: "/assets/frequency-shift/photos/frequency-shift-003-feature.webp",
        alt: "Hands raised beneath blue and magenta beams with the Frequency Shift roundel glowing at GRIDWRKS.",
        href: "https://www.instagram.com/p/DNEDMbduITH/",
        position: "60% center",
      },
      {
        title: "World Cup",
        venue: "GRIDWRKS",
        credit: "Photo credit not listed",
        src: "/assets/frequency-shift/photos/world-cup-feature.webp",
        alt: "DJs framed by a packed GRIDWRKS dancefloor under deep red and pink lighting during the World Cup special.",
        href: "https://www.instagram.com/p/DaBDdIyjhBX/",
      },
      {
        title: "Dopamine",
        venue: "City At Night",
        credit: "Photo: Mystic Does Media",
        src: "/assets/frequency-shift/photos/dopamine-02.webp",
        alt: "Three dancers leaning into the booth rail under saturated green light at City At Night.",
        href: "https://www.instagram.com/p/DWUtWzUjo6R/",
      },
    ],
    motion: {
      eyebrow: "01 / Motion and promotion",
      title: "Cut for the announcement and the afterimage.",
      paragraphs: [
        "I edit every promo video for Frequency Shift. Announcements have a few seconds to get across the date, lineup, venue and where to buy tickets. Recaps show what the night actually felt like.",
        "The Techno Special film comes first. It mixes live footage from GRIDWRKS with a campaign edit meant to sell tickets. This Friday is a short last-call promo cut to the beat. The GRIDWRKS film and the Solstice collab show the other side: vertical-first promo and edits built around the night itself.",
      ],
      slots: [
        {
          number: "01",
          title: "Frequency Shift: Techno Special",
          venue: "GRIDWRKS",
          description: "Featured campaign film / September 4, 2026",
          src: "/assets/frequency-shift/video/frequency-shift-techno-special-master.mp4",
          poster: "/assets/frequency-shift/video/frequency-shift-techno-special-poster.jpg",
          fallbackUrl: "https://frequencyshift.ca/events/september-4/",
          featured: true,
          aspectRatio: "16 / 9",
        },
        {
          number: "02",
          title: "Frequency Shift: This Friday",
          venue: "GRIDWRKS",
          description: "Announcement promo / last call before doors",
          src: "/assets/frequency-shift/video/frequency-shift-this-friday-promo.mp4",
          poster: "/assets/frequency-shift/video/frequency-shift-this-friday-poster.jpg",
          fallbackUrl: "https://frequencyshift.ca/events/",
          featured: true,
          aspectRatio: "16 / 9",
        },
        {
          number: "03",
          title: "Frequency Shift at GRIDWRKS",
          venue: "GRIDWRKS",
          description: "Standalone event film",
          src: "/assets/frequency-shift/video/frequency-shift-gridwrks-master.mov",
          poster: "/assets/frequency-shift/video/frequency-shift-campaign-teaser-poster.jpg",
          fallbackUrl: "https://frequencyshift.ca/archive/frequency-shift-005/",
        },
        {
          number: "04",
          title: "Frequency Shift × Solstice",
          venue: "City At Night",
          description: "Collaboration / live-event film",
          src: "/assets/frequency-shift/video/frequency-shift-solstice-city-at-night-master.mov",
          poster: "/assets/frequency-shift/video/frequency-shift-live-recap-poster.jpg",
          fallbackUrl: "https://frequencyshift.ca/archive/solstice/",
        },
      ],
    },
    web: {
      eyebrow: "04 / Web platform",
      title: "A permanent home for a moving series.",
      paragraphs: [
        "I built frequencyshift.ca and I still run it. Each event page gives you the date, lineup, venue and tickets up front. After the night, the event moves into an archive with photos, credits, collaborators and links to the original posts.",
        "I handle releases, content updates and media as the schedule changes, so the site stays as current as the Instagram.",
      ],
      homeScreenshot: "/assets/frequency-shift/web/home-desktop.webp",
    },
    room: {
      eyebrow: "05 / Inside the room",
      title: "The work continues when doors open.",
      body: "I stay involved through planning and promotion, make last-minute changes to assets, and help out on site. Seeing where people get stuck, what they need to know and how the crowd moves on the night changes how I run the next campaign.",
      dj: "I also DJ some of the nights. It’s a small part of what I do here, but it keeps me close to the people who come.",
    },
    closing: {
      eyebrow: "06 / Continuity",
      title: "From first post to final record.",
      paragraphs: [
        "The first night was in May 2025. Since then, Frequency Shift has added solo editions, partner takeovers and Frequency Fest at Club SAW. The archive credits the artists, venues, collaborators and photographers alongside the work.",
        "I stay with each night the whole way through, from the campaign to the event to the archive, which is why it all still sounds like one series.",
      ],
    },
  },
  seo: {
    title: "Frequency Shift — Creative Direction, Web & Video by Matia Dosen",
    description: "Creative direction, web management, video editing, campaign production, and event support for Ottawa house series Frequency Shift.",
    image: "/assets/frequency-shift/hero-crowd.webp",
  },
}

export default frequencyShift
